import React, { useState, useEffect, useMemo } from "react";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Music as AudioIcon,
  FileText as DocumentIcon,
  FolderArchive as OtherIcon,
  Upload,
  Search,
  Grid,
  List,
  Copy,
  Check,
  Edit2,
  Trash2,
  ExternalLink,
  Eye,
  X,
  Link2,
  RefreshCw,
  FolderOpen,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { useToast } from "../../hooks/otherHooks/useToast";
import { MediaAsset } from "../../services/mediaService";
import {
  useMediaQuery,
  useCreateMediaMutation,
  useUpdateMediaMutation,
  useDeleteMediaMutation,
} from "../../hooks/queries/useMediaQuery";

const MediaLibrary: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const createMutation = useCreateMediaMutation();
  const updateMutation = useUpdateMediaMutation();
  const deleteMutation = useDeleteMediaMutation();

  const queryParams = useMemo(() => ({
    media_type: selectedTypeFilter !== "all" ? selectedTypeFilter : undefined,
  }), [selectedTypeFilter]);

  const {
    data: assets = [],
    isLoading: loading,
    refetch,
  } = useMediaQuery(queryParams);

  // Modals state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [editingAsset, setEditingAsset] = useState<MediaAsset | null>(null);
  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);
  const [deletingAsset, setDeletingAsset] = useState<MediaAsset | null>(null);

  // Form State for Upload & Edit
  const [uploadTab, setUploadTab] = useState<"file" | "url">("file");
  const [title, setTitle] = useState<string>("");
  const [altText, setAltText] = useState<string>("");
  const [caption, setCaption] = useState<string>("");
  const [mediaType, setMediaType] = useState<MediaAsset["media_type"]>("image");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [externalUrl, setExternalUrl] = useState<string>("");
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { addToast } = useToast();

  // Handle live search
  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        (asset.title && asset.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (asset.caption && asset.caption.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (asset.alt_text && asset.alt_text.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType =
        selectedTypeFilter === "all" || asset.media_type === selectedTypeFilter;

      return matchesSearch && matchesType;
    });
  }, [assets, searchQuery, selectedTypeFilter]);

  // Asset Counts
  const assetCounts = useMemo(() => {
    return {
      total: assets.length,
      image: assets.filter((a) => a.media_type === "image").length,
      video: assets.filter((a) => a.media_type === "video").length,
      audio: assets.filter((a) => a.media_type === "audio").length,
      document: assets.filter((a) => a.media_type === "document").length,
      other: assets.filter((a) => a.media_type === "other").length,
    };
  }, [assets]);

  // Copy URL action
  const handleCopyUrl = (asset: MediaAsset) => {
    const targetUrl = asset.url || asset.external_url || "";
    if (!targetUrl) return;
    navigator.clipboard.writeText(targetUrl);
    setCopiedId(asset.id);
    if (addToast) {
      addToast({ message: "Media URL copied to clipboard!", bg: "success" });
    }
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Open Create Modal
  const openCreateModal = (mode: "file" | "url" = "file") => {
    setUploadTab(mode);
    setTitle("");
    setAltText("");
    setCaption("");
    setMediaType("image");
    setSelectedFile(null);
    setExternalUrl("");
    setFilePreviewUrl("");
    setIsUploadModalOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (asset: MediaAsset) => {
    setEditingAsset(asset);
    setTitle(asset.title || "");
    setAltText(asset.alt_text || "");
    setCaption(asset.caption || "");
    setMediaType(asset.media_type || "image");
    setExternalUrl(asset.external_url || "");
    setSelectedFile(null);
    setFilePreviewUrl(asset.url || "");
  };

  // Handle File Change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setTitle(nameWithoutExt);
      }

      if (file.type.startsWith("image/")) setMediaType("image");
      else if (file.type.startsWith("video/")) setMediaType("video");
      else if (file.type.startsWith("audio/")) setMediaType("audio");
      else if (
        file.type.includes("pdf") ||
        file.type.includes("document") ||
        file.type.includes("text")
      )
        setMediaType("document");

      setFilePreviewUrl(URL.createObjectURL(file));
    }
  };

  // Create Submit
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      if (addToast) addToast({ message: "Please provide an asset title.", bg: "danger" });
      return;
    }

    if (uploadTab === "file" && !selectedFile) {
      if (addToast) addToast({ message: "Please choose a media file to upload.", bg: "danger" });
      return;
    }

    if (uploadTab === "url" && !externalUrl.trim()) {
      if (addToast) addToast({ message: "Please enter an external media URL.", bg: "danger" });
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("media_type", mediaType);
      if (altText.trim()) formData.append("alt_text", altText.trim());
      if (caption.trim()) formData.append("caption", caption.trim());

      if (uploadTab === "file" && selectedFile) {
        formData.append("file", selectedFile);
      } else if (uploadTab === "url" && externalUrl.trim()) {
        formData.append("external_url", externalUrl.trim());
      }

      const created = await createMutation.mutateAsync(formData);

      if (addToast) {
        addToast({ message: `Media "${created.title}" successfully added!`, bg: "success" });
      }
      setIsUploadModalOpen(false);
    } catch (err) {
      console.error("Create media failed:", err);
      if (addToast) {
        addToast({ message: "Failed to upload media asset.", bg: "danger" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Update Submit
  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAsset || !title.trim()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("media_type", mediaType);
      if (altText.trim()) formData.append("alt_text", altText.trim());
      if (caption.trim()) formData.append("caption", caption.trim());
      if (externalUrl.trim()) formData.append("external_url", externalUrl.trim());
      if (selectedFile) formData.append("file", selectedFile);

      const updated = await updateMutation.mutateAsync({ id: editingAsset.id, data: formData });

      if (addToast) {
        addToast({ message: `Media "${updated.title}" successfully updated!`, bg: "success" });
      }
      setEditingAsset(null);
    } catch (err) {
      console.error("Update media failed:", err);
      if (addToast) addToast({ message: "Failed to update media asset.", bg: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Confirm
  const handleDeleteConfirm = async () => {
    if (!deletingAsset) return;
    setSubmitting(true);
    try {
      await deleteMutation.mutateAsync(deletingAsset.id);
      if (addToast) {
        addToast({ message: `Media "${deletingAsset.title}" deleted.`, bg: "success" });
      }
      setDeletingAsset(null);
    } catch (err) {
      console.error("Delete media error:", err);
      if (addToast) addToast({ message: "Failed to delete media asset.", bg: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  // Get Media Type Icon
  const getMediaTypeIcon = (type: MediaAsset["media_type"]) => {
    switch (type) {
      case "image":
        return <ImageIcon className="cht-h-4 cht-w-4 cht-text-blue-500" />;
      case "video":
        return <VideoIcon className="cht-h-4 cht-w-4 cht-text-rose-500" />;
      case "audio":
        return <AudioIcon className="cht-h-4 cht-w-4 cht-text-purple-500" />;
      case "document":
        return <DocumentIcon className="cht-h-4 cht-w-4 cht-text-emerald-500" />;
      default:
        return <OtherIcon className="cht-h-4 cht-w-4 cht-text-slate-500" />;
    }
  };

  // Format Date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="cht-space-y-6 cht-pb-12 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header Banner - CoreDesign styled */}
      <div className="cht-rounded-squircle cht-bg-gradient-to-r cht-from-[#0f172a] cht-via-[#1e293b] cht-to-[#3e57da]/80 cht-p-6 md:cht-p-8 cht-text-white cht-relative cht-overflow-hidden cht-shadow-soft">
        <div className="cht-absolute -cht-right-10 -cht-top-10 cht-w-80 cht-h-80 cht-bg-[#3e57da]/20 cht-rounded-full cht-blur-3xl cht-pointer-events-none" />
        <div className="cht-relative cht-z-10 cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-6">
          <div className="cht-space-y-2 cht-max-w-2xl">
            <div className="cht-inline-flex cht-items-center cht-gap-2 cht-bg-white/10 cht-backdrop-blur-md cht-px-3.5 cht-py-1 cht-rounded-full cht-text-xs cht-font-black cht-text-white/90 cht-border cht-border-white/15 cht-uppercase cht-tracking-widest">
              <Sparkles size={12} className="cht-text-[#3e57da]" />
              <span>SuperAdmin Cloud Media & Assets</span>
            </div>
            <h1 className="cht-text-2xl sm:cht-text-4xl cht-font-black cht-tracking-tight cht-text-white">
              Media Library
            </h1>
            <p className="cht-text-xs sm:cht-text-sm cht-text-slate-300 cht-font-medium cht-leading-relaxed">
              Upload, organize, preview, and manage course assets, blog images, track media, certificates, and CDN attachments across Codehub Technologies.
            </p>
          </div>

          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 cht-shrink-0">
            <Button
              variant="primary"
              size="md"
              onClick={() => openCreateModal("file")}
              leftIcon={<Upload size={16} />}
              className="cht-rounded-xl"
            >
              Upload New Media
            </Button>
            <button
              onClick={() => openCreateModal("url")}
              className="cht-inline-flex cht-items-center cht-gap-2 cht-px-5 cht-py-3.5 cht-rounded-xl cht-text-[11px] cht-font-black cht-uppercase cht-tracking-widest cht-bg-white/10 cht-border cht-border-white/20 cht-text-white hover:cht-bg-white/20 cht-transition-all cursor-pointer"
            >
              <Link2 size={16} className="cht-text-[#3e57da]" />
              <span>Add External URL</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stat Counter Pills */}
      <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 lg:cht-grid-cols-6 cht-gap-3">
        {[
          { key: "all", label: "All Assets", count: assetCounts.total, icon: FolderOpen, activeClass: "cht-bg-[#1e293b] cht-text-white cht-border-[#1e293b]" },
          { key: "image", label: "Images", count: assetCounts.image, icon: ImageIcon, activeClass: "cht-bg-blue-600 cht-text-white cht-border-blue-600" },
          { key: "video", label: "Videos", count: assetCounts.video, icon: VideoIcon, activeClass: "cht-bg-rose-600 cht-text-white cht-border-rose-600" },
          { key: "audio", label: "Audio", count: assetCounts.audio, icon: AudioIcon, activeClass: "cht-bg-purple-600 cht-text-white cht-border-purple-600" },
          { key: "document", label: "Documents", count: assetCounts.document, icon: DocumentIcon, activeClass: "cht-bg-emerald-600 cht-text-white cht-border-emerald-600" },
          { key: "other", label: "Other", count: assetCounts.other, icon: OtherIcon, activeClass: "cht-bg-slate-700 cht-text-white cht-border-slate-700" },
        ].map(({ key, label, count, icon: Icon, activeClass }) => {
          const isActive = selectedTypeFilter === key;
          return (
            <div
              key={key}
              onClick={() => setSelectedTypeFilter(key)}
              className={`cht-p-4 cht-rounded-2xl cht-border cht-transition-all cht-duration-200 cht-cursor-pointer cht-shadow-soft ${
                isActive
                  ? `${activeClass} cht-shadow-md`
                  : "cht-bg-white cht-border-gray-200/80 cht-text-slate-800 hover:cht-border-[#3e57da]/30 hover:-cht-translate-y-0.5"
              }`}
            >
              <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-font-bold">
                <span className={`cht-flex cht-items-center cht-gap-1.5 ${isActive ? "cht-text-white cht-font-extrabold" : "cht-text-slate-800"}`}>
                  <Icon className="cht-h-4 cht-w-4" /> {label}
                </span>
                <span
                  className={`cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-xs cht-font-black cht-border ${
                    isActive
                      ? "cht-bg-white/20 cht-text-white cht-border-white/30"
                      : "cht-bg-gray-100 cht-text-slate-700 cht-border-gray-200"
                  }`}
                >
                  {count}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar: Search, Filters & View Toggle */}
      <div className="cht-p-4 cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-2xl cht-shadow-soft cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-relative cht-w-full sm:cht-w-96">
          <Search
            size={16}
            className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
          />
          <input
            type="text"
            placeholder="Search media by title, alt text, or caption..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-h-11 cht-rounded-xl cht-bg-gray-50/80 cht-border cht-border-gray-200 cht-pl-10 cht-pr-9 cht-text-xs cht-font-medium cht-text-gray-900 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)] focus:cht-outline-none cht-transition-all placeholder:cht-text-gray-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="cht-absolute cht-right-3 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 hover:cht-text-gray-600 cursor-pointer"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="cht-flex cht-items-center cht-space-x-2 cht-w-full sm:cht-w-auto cht-justify-between sm:cht-justify-end">
          <button
            onClick={() => refetch()}
            className="cht-h-11 cht-w-11 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200 cht-text-gray-600 hover:cht-text-[#3e57da] hover:cht-bg-[#f2f4fd] cht-transition-all cursor-pointer cht-shrink-0"
            title="Refresh List"
          >
            <RefreshCw size={16} className={loading ? "cht-animate-spin" : ""} />
          </button>

          <div className="cht-flex cht-items-center cht-bg-gray-100 cht-p-1 cht-rounded-xl cht-border cht-border-gray-200 cht-h-11">
            <button
              onClick={() => setViewMode("grid")}
              className={`cht-h-9 cht-px-3 cht-rounded-lg cht-text-xs cht-font-black cht-transition-all cht-flex cht-items-center cht-gap-1.5 cursor-pointer ${
                viewMode === "grid"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                  : "cht-text-gray-500 hover:cht-text-gray-800"
              }`}
              title="Grid View"
            >
              <Grid size={15} />
              <span className="cht-hidden sm:cht-inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`cht-h-9 cht-px-3 cht-rounded-lg cht-text-xs cht-font-black cht-transition-all cht-flex cht-items-center cht-gap-1.5 cursor-pointer ${
                viewMode === "list"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                  : "cht-text-gray-500 hover:cht-text-gray-800"
              }`}
              title="List View"
            >
              <List size={15} />
              <span className="cht-hidden sm:cht-inline">List</span>
            </button>
          </div>
        </div>
      </div>

      {/* Asset Display */}
      {loading ? (
        <div className="cht-py-20 cht-text-center cht-space-y-3">
          <RefreshCw className="cht-h-8 cht-w-8 cht-text-[#3e57da] cht-animate-spin cht-mx-auto" />
          <p className="cht-text-xs cht-text-gray-500 cht-font-medium">Loading media assets...</p>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="cht-py-16 cht-px-6 cht-text-center cht-space-y-4 cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-squircle cht-shadow-soft">
          <div className="cht-h-16 cht-w-16 cht-rounded-full cht-bg-[#f2f4fd] cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-mx-auto">
            <ImageIcon className="cht-h-8 cht-w-8" />
          </div>
          <div className="cht-space-y-1 cht-max-w-sm cht-mx-auto">
            <h3 className="cht-text-base cht-font-bold cht-text-gray-900">No Media Assets Found</h3>
            <p className="cht-text-xs cht-text-gray-500">
              {searchQuery
                ? `No media items matching "${searchQuery}". Try clearing search.`
                : "You haven't uploaded any media assets yet. Click below to add your first asset."}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => openCreateModal("file")}
            leftIcon={<Upload size={16} />}
            className="cht-rounded-xl"
          >
            Upload Asset
          </Button>
        </div>
      ) : viewMode === "grid" ? (
        /* GRID VIEW */
        <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 md:cht-grid-cols-3 lg:cht-grid-cols-4 cht-gap-5">
          {filteredAssets.map((asset) => (
            <div
              key={asset.id}
              className="cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-squircle cht-overflow-hidden cht-shadow-soft hover:cht-shadow-float cht-transition-all cht-duration-300 cht-flex cht-flex-col cht-justify-between cht-group"
            >
              {/* Media Thumbnail Box */}
              <div className="cht-relative cht-aspect-video cht-bg-slate-900 cht-overflow-hidden cht-flex cht-items-center cht-justify-center">
                {asset.media_type === "image" ? (
                  <img
                    src={asset.thumbnail_url || asset.url}
                    alt={asset.alt_text || asset.title}
                    className="cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform cht-duration-300"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                ) : asset.media_type === "video" ? (
                  <div className="cht-w-full cht-h-full cht-flex cht-flex-col cht-items-center cht-justify-center cht-bg-slate-900 cht-text-rose-400 cht-space-y-1">
                    <VideoIcon className="cht-h-10 cht-w-10" />
                    <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-slate-300">Video Asset</span>
                  </div>
                ) : asset.media_type === "audio" ? (
                  <div className="cht-w-full cht-h-full cht-flex cht-flex-col cht-items-center cht-justify-center cht-bg-purple-950 cht-text-purple-400 cht-space-y-1">
                    <AudioIcon className="cht-h-10 cht-w-10" />
                    <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-purple-200">Audio Track</span>
                  </div>
                ) : (
                  <div className="cht-w-full cht-h-full cht-flex cht-flex-col cht-items-center cht-justify-center cht-bg-emerald-950 cht-text-emerald-400 cht-space-y-1">
                    <DocumentIcon className="cht-h-10 cht-w-10" />
                    <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-emerald-200">Document</span>
                  </div>
                )}

                {/* Media Type Badge Overlay */}
                <div className="cht-absolute cht-top-2.5 cht-left-2.5 cht-flex cht-items-center cht-gap-1.5 cht-bg-slate-900/80 cht-backdrop-blur-md cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-text-white cht-border cht-border-white/10 cht-uppercase cht-tracking-wider">
                  {getMediaTypeIcon(asset.media_type)}
                  <span>{asset.media_type}</span>
                </div>

                {/* Hover Quick Actions */}
                <div className="cht-absolute cht-inset-0 cht-bg-slate-950/60 cht-opacity-0 group-hover:cht-opacity-100 cht-transition-opacity cht-backdrop-blur-xs cht-flex cht-items-center cht-justify-center cht-gap-2">
                  <button
                    onClick={() => setPreviewAsset(asset)}
                    className="cht-p-2.5 cht-rounded-full cht-bg-white cht-text-slate-900 hover:cht-scale-110 cht-transition-transform cht-shadow-lg cursor-pointer"
                    title="Preview Media"
                  >
                    <Eye className="cht-h-4 cht-w-4" />
                  </button>
                  <button
                    onClick={() => handleCopyUrl(asset)}
                    className="cht-p-2.5 cht-rounded-full cht-bg-white cht-text-slate-900 hover:cht-scale-110 cht-transition-transform cht-shadow-lg cursor-pointer"
                    title="Copy URL"
                  >
                    {copiedId === asset.id ? <Check className="cht-h-4 cht-w-4 cht-text-emerald-600" /> : <Copy className="cht-h-4 cht-w-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(asset)}
                    className="cht-p-2.5 cht-rounded-full cht-bg-white cht-text-slate-900 hover:cht-scale-110 cht-transition-transform cht-shadow-lg cursor-pointer"
                    title="Edit Metadata"
                  >
                    <Edit2 className="cht-h-4 cht-w-4 cht-text-blue-600" />
                  </button>
                  <button
                    onClick={() => setDeletingAsset(asset)}
                    className="cht-p-2.5 cht-rounded-full cht-bg-white cht-text-slate-900 hover:cht-scale-110 cht-transition-transform cht-shadow-lg cursor-pointer"
                    title="Delete Media"
                  >
                    <Trash2 className="cht-h-4 cht-w-4 cht-text-rose-600" />
                  </button>
                </div>
              </div>

              {/* Card Meta Content */}
              <div className="cht-p-4 cht-space-y-2.5 cht-flex-1 cht-flex cht-flex-col cht-justify-between">
                <div className="cht-space-y-1.5">
                  <h4 className="cht-text-xs cht-font-black cht-text-gray-900 cht-line-clamp-1 cht-truncate" title={asset.title}>
                    {asset.title}
                  </h4>

                  {asset.caption && (
                    <p className="cht-text-[11px] cht-text-gray-500 cht-line-clamp-2 cht-leading-snug">
                      {asset.caption}
                    </p>
                  )}

                  {(asset.url || asset.external_url) && (
                    <div className="cht-pt-0.5">
                      <a
                        href={asset.url || asset.external_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="cht-text-[#3e57da] hover:cht-underline cht-inline-flex cht-items-center cht-gap-1 cht-font-mono cht-text-[10px] cht-truncate cht-max-w-full cht-font-semibold"
                        title={asset.url || asset.external_url}
                      >
                        <ExternalLink className="cht-h-3 cht-w-3 cht-shrink-0 cht-text-[#3e57da]" />
                        <span className="cht-truncate">{asset.url || asset.external_url}</span>
                      </a>
                    </div>
                  )}
                </div>

                {/* Uploaded By & Date Footer */}
                <div className="cht-pt-2.5 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-text-[10px]">
                  {asset.uploaded_by ? (
                    <div className="cht-flex cht-items-center cht-space-x-1.5 cht-truncate cht-max-w-[130px]">
                      <div className="cht-h-5 cht-w-5 cht-rounded-full cht-bg-[#f2f4fd] cht-text-[#3e57da] cht-overflow-hidden cht-flex cht-items-center cht-justify-center cht-border cht-border-gray-200 cht-shrink-0">
                        {asset.uploaded_by.get_photo_url || asset.uploaded_by.photo_url ? (
                          <img
                            src={asset.uploaded_by.get_photo_url || asset.uploaded_by.photo_url || ""}
                            alt={asset.uploaded_by.first_name}
                            className="cht-h-full cht-w-full cht-object-cover"
                          />
                        ) : (
                          <span className="cht-text-[9px] cht-font-black">
                            {(asset.uploaded_by.first_name?.[0] || "U").toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="cht-text-slate-700 cht-font-bold cht-truncate">
                        {asset.uploaded_by.first_name} {asset.uploaded_by.last_name || ""}
                      </span>
                    </div>
                  ) : (
                    <span className="cht-text-gray-400 cht-font-medium">Codehub System</span>
                  )}

                  <span className="cht-text-gray-400 cht-font-medium cht-shrink-0">{formatDate(asset.uploaded_at)}</span>
                </div>

                {/* Action Buttons */}
                <div className="cht-pt-2 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-text-[11px]">
                  <button
                    onClick={() => handleCopyUrl(asset)}
                    className="cht-text-[#3e57da] cht-font-bold hover:cht-underline cht-inline-flex cht-items-center cht-gap-1 cursor-pointer"
                  >
                    {copiedId === asset.id ? <Check className="cht-h-3.5 cht-w-3.5 cht-text-emerald-600" /> : <Copy className="cht-h-3.5 cht-w-3.5" />}
                    <span>{copiedId === asset.id ? "Copied!" : "Copy Link"}</span>
                  </button>

                  <div className="cht-flex cht-items-center cht-space-x-1">
                    <button
                      onClick={() => setPreviewAsset(asset)}
                      className="cht-p-1 cht-text-gray-500 hover:cht-text-[#3e57da] cht-rounded-md hover:cht-bg-gray-100 cursor-pointer"
                      title="Preview"
                    >
                      <Eye className="cht-h-3.5 cht-w-3.5" />
                    </button>
                    <button
                      onClick={() => openEditModal(asset)}
                      className="cht-p-1 cht-text-blue-600 hover:cht-text-blue-800 cht-rounded-md hover:cht-bg-blue-50 cursor-pointer"
                      title="Edit"
                    >
                      <Edit2 className="cht-h-3.5 cht-w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeletingAsset(asset)}
                      className="cht-p-1 cht-text-rose-600 hover:cht-text-rose-800 cht-rounded-md hover:cht-bg-rose-50 cursor-pointer"
                      title="Delete"
                    >
                      <Trash2 className="cht-h-3.5 cht-w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* LIST VIEW (adheres to GEMINI.md table spec: no vertical borders, 56px height) */
        <div className="cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-squircle cht-overflow-hidden cht-shadow-soft">
          <div className="cht-overflow-x-auto">
            <table className="cht-w-full cht-text-left cht-text-xs">
              <thead className="cht-bg-gray-50/70 cht-border-b cht-border-gray-100 cht-text-gray-500 cht-font-black cht-uppercase cht-tracking-widest cht-text-[10px]">
                <tr>
                  <th className="cht-py-3.5 cht-px-5">Asset</th>
                  <th className="cht-py-3.5 cht-px-5">Type</th>
                  <th className="cht-py-3.5 cht-px-5">Uploaded By</th>
                  <th className="cht-py-3.5 cht-px-5">Uploaded Date</th>
                  <th className="cht-py-3.5 cht-px-5">URL / Link</th>
                  <th className="cht-py-3.5 cht-px-5 cht-text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-100 cht-font-medium cht-text-gray-800">
                {filteredAssets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="hover:cht-bg-gray-50/60 cht-transition-colors"
                    style={{ height: 56 }}
                  >
                    <td className="cht-py-2 cht-px-5">
                      <div className="cht-flex cht-items-center cht-space-x-3">
                        <div className="cht-h-10 cht-w-12 cht-rounded-lg cht-bg-slate-900 cht-overflow-hidden cht-shrink-0 cht-flex cht-items-center cht-justify-center">
                          {asset.media_type === "image" ? (
                            <img
                              src={asset.thumbnail_url || asset.url}
                              alt={asset.title}
                              className="cht-w-full cht-h-full cht-object-cover"
                            />
                          ) : (
                            getMediaTypeIcon(asset.media_type)
                          )}
                        </div>
                        <div className="cht-truncate cht-max-w-xs">
                          <p className="cht-font-bold cht-text-gray-900 cht-truncate">{asset.title}</p>
                          {asset.alt_text && <p className="cht-text-[10px] cht-text-gray-400 cht-truncate">Alt: {asset.alt_text}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="cht-py-2 cht-px-5">
                      <span className="cht-inline-flex cht-items-center cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-gray-100 cht-text-gray-800 cht-border cht-border-gray-200">
                        {asset.media_type}
                      </span>
                    </td>
                    <td className="cht-py-2 cht-px-5 cht-whitespace-nowrap">
                      {asset.uploaded_by ? (
                        <div className="cht-flex cht-items-center cht-space-x-2">
                          <div className="cht-h-7 cht-w-7 cht-rounded-full cht-bg-[#f2f4fd] cht-text-[#3e57da] cht-overflow-hidden cht-flex cht-items-center cht-justify-center cht-border cht-border-gray-200 cht-shrink-0">
                            {asset.uploaded_by.get_photo_url || asset.uploaded_by.photo_url ? (
                              <img
                                src={asset.uploaded_by.get_photo_url || asset.uploaded_by.photo_url || ""}
                                alt={asset.uploaded_by.first_name}
                                className="cht-h-full cht-w-full cht-object-cover"
                              />
                            ) : (
                              <span className="cht-text-[10px] cht-font-black">
                                {(asset.uploaded_by.first_name?.[0] || "U").toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="cht-truncate cht-max-w-[150px]">
                            <p className="cht-text-xs cht-font-bold cht-text-gray-900 cht-leading-tight cht-truncate">
                              {asset.uploaded_by.first_name} {asset.uploaded_by.last_name || ""}
                            </p>
                            <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium cht-leading-tight cht-truncate">
                              {asset.uploaded_by.role || asset.uploaded_by.email || "User"}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <span className="cht-text-gray-400 cht-text-xs">—</span>
                      )}
                    </td>
                    <td className="cht-py-2 cht-px-5 cht-text-gray-500 cht-whitespace-nowrap">
                      {formatDate(asset.uploaded_at)}
                    </td>
                    <td className="cht-py-2 cht-px-5 cht-max-w-xs cht-truncate">
                      <a
                        href={asset.url || asset.external_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="cht-text-[#3e57da] hover:cht-underline cht-inline-flex cht-items-center cht-gap-1 cht-font-mono cht-text-[11px] cht-truncate cht-font-semibold"
                      >
                        <ExternalLink className="cht-h-3 cht-w-3 cht-shrink-0" />
                        <span className="cht-truncate">{asset.url || asset.external_url}</span>
                      </a>
                    </td>
                    <td className="cht-py-2 cht-px-5 cht-text-right cht-whitespace-nowrap">
                      <div className="cht-inline-flex cht-items-center cht-space-x-1">
                        <button
                          onClick={() => handleCopyUrl(asset)}
                          className="cht-p-1.5 cht-text-gray-600 hover:cht-text-[#3e57da] cht-rounded-lg hover:cht-bg-gray-100 cursor-pointer"
                          title="Copy Link"
                        >
                          {copiedId === asset.id ? <Check className="cht-h-4 cht-w-4 cht-text-emerald-600" /> : <Copy className="cht-h-4 cht-w-4" />}
                        </button>
                        <button
                          onClick={() => setPreviewAsset(asset)}
                          className="cht-p-1.5 cht-text-gray-600 hover:cht-text-gray-900 cht-rounded-lg hover:cht-bg-gray-100 cursor-pointer"
                          title="Preview"
                        >
                          <Eye className="cht-h-4 cht-w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(asset)}
                          className="cht-p-1.5 cht-text-blue-600 hover:cht-text-blue-800 cht-rounded-lg hover:cht-bg-blue-50 cursor-pointer"
                          title="Edit"
                        >
                          <Edit2 className="cht-h-4 cht-w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingAsset(asset)}
                          className="cht-p-1.5 cht-text-rose-600 hover:cht-text-rose-800 cht-rounded-lg hover:cht-bg-rose-50 cursor-pointer"
                          title="Delete"
                        >
                          <Trash2 className="cht-h-4 cht-w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREATE MODAL */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Add New Media Asset"
        size="lg"
      >
        <div className="cht-flex cht-items-center cht-space-x-2 cht-bg-gray-100 cht-p-1 cht-rounded-xl cht-mb-5">
          <button
            type="button"
            onClick={() => setUploadTab("file")}
            className={`cht-flex-1 cht-py-2 cht-text-xs cht-font-black cht-rounded-lg cht-transition-all cursor-pointer ${
              uploadTab === "file"
                ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                : "cht-text-gray-500 hover:cht-text-gray-800"
            }`}
          >
            File Upload
          </button>
          <button
            type="button"
            onClick={() => setUploadTab("url")}
            className={`cht-flex-1 cht-py-2 cht-text-xs cht-font-black cht-rounded-lg cht-transition-all cursor-pointer ${
              uploadTab === "url"
                ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                : "cht-text-gray-500 hover:cht-text-gray-800"
            }`}
          >
            External URL Link
          </button>
        </div>

        <form onSubmit={handleCreateSubmit} className="cht-space-y-4 cht-text-xs">
          <Input
            label="Asset Title"
            required
            placeholder="e.g. Fullstack Web Bootcamp Promo Banner"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="cht-flex cht-flex-col cht-w-full">
            <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
              Media Type <span className="cht-text-red-500">*</span>
            </label>
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value as MediaAsset["media_type"])}
              className="cht-h-11 cht-text-xs cht-rounded-2xl cht-border cht-border-gray-200/90 cht-bg-white cht-px-3.5 cht-w-full cht-font-bold cht-text-gray-800 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)] focus:cht-outline-none"
            >
              <option value="image">Image (PNG, JPG, WebP, SVG)</option>
              <option value="video">Video (MP4, WebM)</option>
              <option value="audio">Audio (MP3, WAV)</option>
              <option value="document">Document (PDF, DOCX)</option>
              <option value="other">Other Assets</option>
            </select>
          </div>

          {uploadTab === "file" ? (
            <div className="cht-flex cht-flex-col cht-w-full">
              <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
                Choose Media File <span className="cht-text-red-500">*</span>
              </label>
              <input
                type="file"
                required
                onChange={handleFileChange}
                className="cht-block cht-w-full cht-text-xs cht-text-gray-500 file:cht-mr-4 file:cht-py-2.5 file:cht-px-4 file:cht-rounded-xl file:cht-border-0 file:cht-text-xs file:cht-font-bold file:cht-bg-[#f2f4fd] file:cht-text-[#3e57da] hover:file:cht-bg-[#3e57da]/10 cursor-pointer cht-border cht-border-gray-200 cht-rounded-2xl cht-p-1.5 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)]"
              />
              {filePreviewUrl && mediaType === "image" && (
                <div className="cht-mt-2.5 cht-w-24 cht-h-16 cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200">
                  <img src={filePreviewUrl} alt="Preview" className="cht-w-full cht-h-full cht-object-cover" />
                </div>
              )}
            </div>
          ) : (
            <Input
              label="External Media URL"
              required
              type="url"
              placeholder="https://res.cloudinary.com/... or https://images.unsplash.com/..."
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              leftIcon={<Link2 size={16} />}
            />
          )}

          <Input
            label="Alt Text (Accessibility & SEO)"
            placeholder="e.g. Students participating in modern computer lab training"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
          />

          <div className="cht-flex cht-flex-col cht-w-full">
            <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
              Caption / Description
            </label>
            <textarea
              rows={3}
              placeholder="Brief description or caption for this media asset..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="cht-w-full cht-text-xs cht-rounded-2xl cht-border cht-border-gray-200/90 cht-p-3.5 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-outline-none focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)] cht-transition-all"
            />
          </div>

          <div className="cht-pt-3 cht-flex cht-items-center cht-justify-end cht-space-x-3 cht-border-t cht-border-gray-100">
            <button
              type="button"
              onClick={() => setIsUploadModalOpen(false)}
              className="cht-text-xs cht-font-bold cht-rounded-xl cht-h-11 cht-px-5 cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200 cursor-pointer"
            >
              Cancel
            </button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Uploading..." : "Save Media Asset"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        isOpen={Boolean(editingAsset)}
        onClose={() => setEditingAsset(null)}
        title="Edit Media Asset Metadata"
        size="lg"
      >
        <form onSubmit={handleUpdateSubmit} className="cht-space-y-4 cht-text-xs">
          <Input
            label="Asset Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="cht-flex cht-flex-col cht-w-full">
            <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
              Media Type
            </label>
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value as MediaAsset["media_type"])}
              className="cht-h-11 cht-text-xs cht-rounded-2xl cht-border cht-border-gray-200/90 cht-bg-white cht-px-3.5 cht-w-full cht-font-bold cht-text-gray-800 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)] focus:cht-outline-none"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="document">Document</option>
              <option value="other">Other</option>
            </select>
          </div>

          <Input
            label="External URL"
            type="url"
            placeholder="https://..."
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
          />

          <Input
            label="Alt Text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
          />

          <div className="cht-flex cht-flex-col cht-w-full">
            <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
              Caption / Description
            </label>
            <textarea
              rows={3}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="cht-w-full cht-text-xs cht-rounded-2xl cht-border cht-border-gray-200/90 cht-p-3.5 cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] focus:cht-outline-none focus:cht-border-[#3e57da] focus:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)]"
            />
          </div>

          <div className="cht-pt-3 cht-flex cht-items-center cht-justify-end cht-space-x-3 cht-border-t cht-border-gray-100">
            <button
              type="button"
              onClick={() => setEditingAsset(null)}
              className="cht-text-xs cht-font-bold cht-rounded-xl cht-h-11 cht-px-5 cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200 cursor-pointer"
            >
              Cancel
            </button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Saving..." : "Update Asset"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* PREVIEW MODAL */}
      <Modal
        isOpen={Boolean(previewAsset)}
        onClose={() => setPreviewAsset(null)}
        title={previewAsset?.title}
        icon={previewAsset ? getMediaTypeIcon(previewAsset.media_type) : undefined}
        size="xl"
      >
        {previewAsset && (
          <div className="cht-space-y-4">
            <div className="cht-aspect-video cht-bg-slate-950 cht-rounded-2xl cht-overflow-hidden cht-flex cht-items-center cht-justify-center cht-border cht-border-gray-200">
              {previewAsset.media_type === "image" ? (
                <img
                  src={previewAsset.url || previewAsset.external_url || ""}
                  alt={previewAsset.alt_text || previewAsset.title}
                  className="cht-w-full cht-h-full cht-object-contain"
                />
              ) : previewAsset.media_type === "video" ? (
                <video
                  src={previewAsset.url || previewAsset.external_url || ""}
                  controls
                  className="cht-w-full cht-h-full cht-object-contain"
                />
              ) : previewAsset.media_type === "audio" ? (
                <audio
                  src={previewAsset.url || previewAsset.external_url || ""}
                  controls
                  className="cht-w-4/5"
                />
              ) : (
                <div className="cht-text-center cht-space-y-3 cht-p-6">
                  <DocumentIcon className="cht-h-12 cht-w-12 cht-text-emerald-500 cht-mx-auto" />
                  <p className="cht-text-xs cht-text-slate-300 cht-font-mono cht-truncate cht-max-w-md">
                    {previewAsset.url || previewAsset.external_url}
                  </p>
                  <a
                    href={previewAsset.url || previewAsset.external_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-text-[#3e57da] hover:cht-underline cht-font-black"
                  >
                    <span>Open External Document</span>
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>

            <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-pt-2">
              <div className="cht-space-y-1">
                {previewAsset.alt_text && (
                  <p className="cht-text-gray-500">
                    <strong className="cht-text-gray-800">Alt:</strong> {previewAsset.alt_text}
                  </p>
                )}
                {previewAsset.caption && (
                  <p className="cht-text-gray-600 cht-text-xs">
                    {previewAsset.caption}
                  </p>
                )}
              </div>

              <Button
                variant="primary"
                size="sm"
                onClick={() => handleCopyUrl(previewAsset)}
                leftIcon={<Copy size={14} />}
                className="cht-rounded-xl"
              >
                Copy Media Link
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        isOpen={Boolean(deletingAsset)}
        onClose={() => setDeletingAsset(null)}
        size="sm"
        showCloseButton={false}
      >
        {deletingAsset && (
          <div className="cht-text-center cht-space-y-4">
            <div className="cht-h-14 cht-w-14 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center cht-mx-auto">
              <Trash2 size={26} />
            </div>

            <div className="cht-space-y-1">
              <h3 className="cht-text-base cht-font-black cht-text-gray-900">
                Delete Media Asset?
              </h3>
              <p className="cht-text-xs cht-text-gray-500">
                Are you sure you want to delete <strong className="cht-text-gray-800">"{deletingAsset.title}"</strong>? This action cannot be undone.
              </p>
            </div>

            <div className="cht-pt-2 cht-flex cht-items-center cht-justify-center cht-space-x-3">
              <button
                type="button"
                onClick={() => setDeletingAsset(null)}
                className="cht-text-xs cht-font-bold cht-rounded-xl cht-h-11 cht-px-5 cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200 cursor-pointer"
              >
                Cancel
              </button>
              <Button
                variant="danger"
                onClick={handleDeleteConfirm}
                isLoading={submitting}
                className="cht-rounded-xl"
              >
                {submitting ? "Deleting..." : "Yes, Delete Media"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MediaLibrary;
